import { FormikValues, useFormikContext } from 'formik';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { usePergunta } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Icone from 'Component/Icone';

import Ajuda from './Ajuda';
import { FormContexto } from './Form';
import Grupo from './Grupo';
import Rotulo from './Rotulo';

interface Props {
  ajuda?: string;
  name: string;
  rotulo?: string;
  semMargem?: boolean;
  prefixar?: string | React.ReactElement;
  acrescentarBotao?: React.ReactElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  opcoes?: Array<{ valor?: number | string; rotulo: string }> | any;
  padrao?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aoAtualizar?: (valor: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forcarValor?: any;
  busca?: string;
  buscar?: (termo: string) => void;
  buscando?: boolean;
  multiple?: boolean;
  inicial?: string | number | null;
}

const Selecao = ({
  ajuda,
  busca,
  buscar,
  buscando = false,
  name,
  rotulo,
  prefixar,
  acrescentarBotao,
  opcoes,
  padrao = 'Selecione uma opção',
  multiple = false,
  inicial,
  ...props
}: Props): React.ReactElement => {
  const pergunta = usePergunta();
  const { emLinha } = useContext(FormContexto);
  const [desabilitado, setDesabilitado] = useState<boolean>(false);
  const {
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldValue,
    setValues,
    initialValues,
  } = useFormikContext<FormikValues>();
  const [opts, setOpts] = useState<
    { valor?: number | string; rotulo: string }[]
  >([]);

  useEffect(() => {
    setOpts([{ rotulo: padrao, valor: '' }, ...(opcoes || [])]);
  }, [opcoes, padrao]);

  useEffect(() => {
    if (props.forcarValor) {
      setFieldValue(name, props.forcarValor, true);
      setDesabilitado(true);
    }
  }, [name, props.forcarValor, setFieldValue]);

  useEffect(() => {
    if (name.includes('[_ids]')) {
      const chave = name.replace('[_ids]', '');
      const ids: string[] = [];

      if (values[chave] && Array.isArray(values[chave])) {
        values[chave].map((item: { id: string }) => ids.push(item.id));
      }

      setValues(
        {
          ...values,
          [`${chave}`]: {
            _ids: ids,
          },
        },
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (inicial) {
      setFieldValue(name, inicial, false);
    }
  }, [inicial, name, opts, setFieldValue]);

  const atualizarOpcoes = useCallback(() => {
    if (buscar) {
      setFieldValue(name, '', false);

      pergunta({
        titulo: 'Buscar registros',
        padrao: busca,
        placeholder: 'Digite o que deseja buscar',
        botao: 'Buscar',
        aoConfirmar: (busca) => buscar(busca),
      });
    }
  }, [busca, buscar, name, pergunta, setFieldValue]);

  return (
    <Grupo
      semMargem={emLinha || props.semMargem}
      prefixar={prefixar}
      emLinha={emLinha}
      {...(buscar
        ? {
            acrescentarBotao: (
              <Botao tema="primary" onClick={atualizarOpcoes}>
                <Icone nome="search" />
              </Botao>
            ),
          }
        : { acrescentarBotao: acrescentarBotao })}
    >
      <select
        name={name}
        className={`form-select ${buscar && 'rounded-end-0'} ${
          errors[name] && touched[name] && 'is-invalid'
        }`}
        onChange={(e) => {
          if (props.aoAtualizar) {
            props.aoAtualizar(e.currentTarget.value);
          }

          handleChange(e);
        }}
        onBlur={handleBlur}
        value={
          !name.includes('[_ids]')
            ? values[name]
            : values[name.replace('[_ids]', '')]?._ids
        }
        multiple={multiple}
        {...(desabilitado || buscando ? { disabled: true } : {})}
      >
        {opts.map((opcao, chave) => (
          <option key={chave.toString()} value={opcao.valor}>
            {opcao.rotulo}
          </option>
        ))}
      </select>
      <React.Fragment>
        {rotulo && !emLinha && <Rotulo name={name}>{rotulo}</Rotulo>}
      </React.Fragment>
      <React.Fragment>{ajuda && <Ajuda>{ajuda}</Ajuda>}</React.Fragment>
      <div className="invalid-tooltip">
        {errors[name] && touched[name] && errors[name]}
      </div>
    </Grupo>
  );
};

export default Selecao;
